
import { create } from 'zustand';
import type { OdooSession } from '@/types';
import { odooClient } from '@/services/odoo-client';
import { appConfig } from '@/config/app-config';
import { useNotificationStore } from '@/stores/notification-store';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  session: OdooSession | null;
  odooUrl: string;
  odooDb: string;
  error: string | null;

  login: (url: string, db: string, login: string, password: string) => Promise<void>;
  restoreSession: () => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  isAuthenticated: false,
  isLoading: false,
  session: null,
  odooUrl: appConfig.odooUrl,
  odooDb: appConfig.odooDb,
  error: null,

  login: async (url, db, login, password) => {
    set({ isLoading: true, error: null });
    try {
      const session = await odooClient.authenticate({ url, db, login, password });
      set({
        isAuthenticated: true,
        session,
        odooUrl: url,
        odooDb: db,
        isLoading: false,
      });
      // Notify successful login
      useNotificationStore.getState().addNotification({
        type: 'success',
        title: 'تم تسجيل الدخول بنجاح',
        message: `مرحباً ${session.name}`,
        duration: 4000,
      });
    } catch (error: any) {
      set({
        isAuthenticated: false,
        session: null,
        error: error.message || 'فشل تسجيل الدخول',
        isLoading: false,
      });
      throw error;
    }
  },

  restoreSession: async () => {
    set({ isLoading: true, error: null });
    try {
      const isValid = await odooClient.restoreSession();
      if (isValid) {
        const saved = localStorage.getItem('odoo_session');
        const session = saved ? JSON.parse(saved) : null;
        set({
          isAuthenticated: true,
          session,
          odooUrl: appConfig.odooUrl || odooClient.currentUrl,
          odooDb: appConfig.odooDb || odooClient.currentDb,
          isLoading: false,
        });
      } else {
        // Even if session validation fails, keep connected if we have credentials
        const savedSession = localStorage.getItem('odoo_session');
        if (savedSession) {
          const session = JSON.parse(savedSession);
          if (session?.session_id && session?.uid) {
            odooClient['sessionId'] = session.session_id;
            odooClient['uid'] = session.uid;
            const creds = localStorage.getItem('odoo_credentials');
            if (creds) {
              const c = JSON.parse(creds);
              odooClient['url'] = c.url;
              odooClient['db'] = c.db;
            }
            set({
              isAuthenticated: true,
              session,
              odooUrl: odooClient.currentUrl,
              odooDb: odooClient.currentDb,
              isLoading: false,
            });
            return;
          }
        }
        set({ isAuthenticated: false, session: null, isLoading: false });
      }
    } catch {
      // On network error, still try to keep session if we have one
      const savedSession = localStorage.getItem('odoo_session');
      if (savedSession) {
        const session = JSON.parse(savedSession);
        if (session?.session_id && session?.uid) {
          odooClient['sessionId'] = session.session_id;
          odooClient['uid'] = session.uid;
          const creds = localStorage.getItem('odoo_credentials');
          if (creds) {
            const c = JSON.parse(creds);
            odooClient['url'] = c.url;
            odooClient['db'] = c.db;
          }
          set({
            isAuthenticated: true,
            session,
            odooUrl: odooClient.currentUrl,
            odooDb: odooClient.currentDb,
            isLoading: false,
          });
          return;
        }
      }
      set({ isAuthenticated: false, session: null, isLoading: false });
    }
  },

  logout: () => {
    odooClient.logout();
    set({ isAuthenticated: false, session: null, error: null });
    // Notify logout
    useNotificationStore.getState().addNotification({
      type: 'info',
      title: 'تم تسجيل الخروج',
      message: 'تم تسجيل خروجك بنجاح',
      duration: 3000,
    });
  },

  clearError: () => set({ error: null }),
}));
